import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import TableLayout from "../../../layouts/TableLayout/NewTableLayout";
import { useFetchAll } from "../../../hooks/useFetchAll";
import { useListLeadsQuery } from "../../../redux/services/leads/leadsApi";

import { COLUMN_DEFINITIONS, PAGE_SIZE_OPTIONS } from "./table.config";
import { LeadsStatusFilterList } from "./LeadsStatusFilterList";

/**
 * Super Admin Leads inbox.
 *
 * Lists every lead captured from the public marketing form so the
 * team can triage, assign, and respond. The page is built on the
 * same `TableLayout` + `useFetchAll` stack as Jobs/Manifests/Payouts
 * so global search and the Status filter chip are baked into the
 * table header (no bespoke filter UI on this page).
 *
 * Status changes, assignment, and notes happen on the detail page
 * so there's a single place where the activity log is updated.
 */
const Leads = () => {
  const navigate = useNavigate();

  const filterTypes = useMemo(
    () => [
      {
        id: "dateCreated",
        type: "date",
        label: "Submitted",
        defaultText: "All Time",
        defaultValue: { id: "allTime", value: null },
      },
      {
        id: "status",
        type: "multiple",
        label: "Status",
        defaultText: "All",
        content: LeadsStatusFilterList,
      },
    ],
    []
  );

  const {
    pagination: { pageIndex, pageSize, setPagination },
    search: { handleGlobalSearch },
    filters: { setFilters },
    queryResult: { data, isLoading },
  } = useFetchAll({
    queryHook: useListLeadsQuery,
    defaultPageSize: 25,
  });

  const handleEdit = useCallback(
    (itemId) => {
      navigate(`/admin/leads/${itemId}`);
    },
    [navigate]
  );

  return (
    <div className="w-full h-full min-h-0">
      <TableLayout
        id="leads"
        title="Leads"
        buttonSize="xs"
        loading={isLoading}
        columns={COLUMN_DEFINITIONS}
        data={data?.leads}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        pagination={data}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPaginationChange={setPagination}
        handleEdit={handleEdit}
        hideMainAction
        noDelete
        globalSearchFilters={["companyName", "contactName", "email"]}
        fullTextSearch={handleGlobalSearch}
        filters={filterTypes}
        onFiltersChange={setFilters}
        hideCheckbox
      />
    </div>
  );
};

export default Leads;
